<template>
  <div class="app" :class="{
    'app-header-menu-search-toggled': getters.appHeaderSearchToggled,
    'app-sidebar-minified': getters.appSidebarMinified,
    'app-sidebar-collapsed': getters.appSidebarCollapsed,
    'app-sidebar-mobile-toggled': getters.appSidebarMobileToggled,
    'app-sidebar-mobile-closed': getters.appSidebarMobileClosed,
    'app-content-full-height': getters.appContentFullHeight,
    'app-content-full-width': getters.appSidebarHide,
    'app-without-sidebar': getters.appSidebarHide,
    'app-without-header': getters.appHeaderHide,
    'app-boxed-layout': getters.appBoxedLayout,
    'app-footer-fixed': getters.appFooterFixed,
  }" style="height: 100%">
    <vue3-progress-bar />
    <AutoLogin />
    <AppHeader v-if="!getters.appHeaderHide && !isIntro" />
    <AppSidebar v-if="!getters.appSidebarHide && !isIntro" />
    <div class="app-content" v-bind:class="getters.appContentClass" style="padding-left: 1rem; padding-right: 1rem;">
      <router-view></router-view>
    </div>
    <AppFooter v-if="getters.appFooter && !isIntro && !isSetting" />
    <PopAlert ref="popAlert" />
    <PopToast />
    <!-- 화면 꽉 차는 logo2 이미지 -->
    <!-- <div class="fullscreen-logo" style="position: fixed; top: 0; left: 0; width: 100vw; height: 100vh; z-index: 9999; background-color: #ebeef4; display: flex; align-items: center; justify-content: center;">
      <img :src="logo2" alt="INSHOW" style="width: 200px; height: 80px; margin-top: 21px;" />
    </div> -->
    <PopSpinner v-if="spinnerVisible" :loadingText="loadingText" />
  </div>
</template>

<script setup>
import { client as AxiosClient } from '@/api';
import { useMQTT } from 'mqtt-vue-hook';
import { useProgress } from '@marcoschulte/vue3-progress';
import { onMounted, onBeforeUnmount } from '@vue/runtime-core';
import { ref, computed, getCurrentInstance, watch, reactive } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { isMobile } from 'mobile-device-detect';
import useMqttControl from '@/common/hooks/useMqttControl';
import router from './router';
import AutoLogin from '@/common/auth/AutoLogin.vue';
import AppSidebar from '@/common/template/components/app/Sidebar.vue';
import AppHeader from '@/common/template/components/app/Header.vue';
import AppFooter from '@/common/template/components/app/Footer.vue';
import AppThemePanel from '@/common/template/components/app/ThemePanel.vue';
import logo2 from '@/assets/logo2.png';
import PopAlert from '@/common/components/popup/PopAlert.vue';
import PopToast from '@/common/components/popup/PopToast.vue';
import PopSpinner from '@/common/components/popup/PopSpinner.vue';

const store = useStore();
const route = useRoute();
const getters = computed(() => store.getters);
const controllers = computed(
  () => getters.value['home/getLowVersionControllers'],
);
let isShowedFirmwarePopup = false;
const isIntro = computed(() => route.path === '/');
const isCertified = computed(() => store.getters["isCertified"]);
const isLogin = computed(() => store.getters["isLogin"]);
const isSetting = computed(() => route.path.includes('/settings'));
const isWidraw = computed(() => route.path.includes('/delete/user'));
const { emitter, logger, mqtt } =
  getCurrentInstance().appContext.config.globalProperties;
const popAlert = ref();
const spinnerVisible = ref(false);
const loadingCount = ref(0);
const loadingText = ref('');
const progress = ref(undefined);
const mqttControl = reactive(useMqttControl());
let touchMoveHandler = null;

// 사이드 메뉴 URL 목록 (1depth)
const sidebarMenuUrls = ['/rooms', '/notices', '/settings', '/profile', '/share', '/update'];

// 현재 경로가 사이드 메뉴 1depth 섹션인지 확인 (정확히 일치)
const isSidebarMenu1DepthExact = (path) => {
  return sidebarMenuUrls.some(url => path === url || path === url + '/');
};

// 현재 경로가 사이드 메뉴 2depth+인지 확인
const isSidebarMenu2DepthPlus = (path) => {
  return sidebarMenuUrls.some(url => {
    return path.startsWith(url + '/') && path !== url && path !== url + '/';
  });
};

// 브라우저 백 버튼 처리 (popstate 이벤트)
// 기본 브라우저 내비게이션을 사용하므로 특별한 처리 불필요
const handlePopState = (event) => {
  // 브라우저가 자동으로 히스토리를 처리하므로 별도 처리 불필요
};

AxiosClient.interceptors.request.use(
  (req) => {
    // hideSpinner가 true인 경우 spinner 표시하지 않음
    if (!req.hideLoading) {
      spinnerVisible.value = true;
      loadingCount.value++;
      console.log('loadingCount', loadingCount.value);
    }
    return req;
  },
  (err) => {
    return Promise.reject(err);
  },
);

AxiosClient.interceptors.response.use(
  (response) => {
    loadingCount.value--;
    loadingCount.value = loadingCount.value < 0 ? 0 : loadingCount.value;
    console.log('loadingCount', loadingCount.value);
    if (loadingCount.value === 0) {
      spinnerVisible.value = false;
      loadingText.value = '';
    }

    if (response.data?.resultCode === '0000') {
      // console.log(JSON.stringify(response.data));
    } else {
      console.warn(JSON.stringify(response.data?.resultCode, response.data?.resultMessage));
    }

    if (response?.data?.resultCode === '9001') {
      return {
        ...response,
        data: {
          ...response.data,
          resultCode: '0000',
          resultMessage: '',
        },
      };
    }
    return response;
  },
  (err) => {
    loadingCount.value--;
    console.log('loadingCount', loadingCount.value);
    if (loadingCount.value === 0) {
      spinnerVisible.value = false;
      loadingText.value = '';
    }

    console.error(err);
    if (
      err.response &&
      err.response.status &&
      (err.response.status === 401 || err.response.status === 419)
    ) {
      emitter.emit('showPopAlert', {
        description: '토큰이 만료되었습니다.\n다시 로그인 해주세요.',
        okCallback: () => {
          router.replace('/login');
          store.dispatch('logout');
        },
        cancelCallback: () => {
          router.replace('/login');
          store.dispatch('logout');
        },
      });
    }
    return Promise.reject(err);
  },
);

onMounted(async () => {
  window.oncontextmenu = function () { return false; }
  console.log('App.onMounted');
  console.log(popAlert.value.textContent);
  if (window.addEventListener) {
    window.addEventListener('message', onReceiveMessage, false);
    console.log('App.onMounted => addEventListener');
  } else if (window.attachEvent) {
    window.attachEvent('message', onReceiveMessage, false);
    console.log('App.onMounted => attachEvent');
  }

  window.addEventListener("flutterInAppWebViewPlatformReady", function (event) {
    console.log("flutterInAppWebViewPlatformReady");
    store.commit("home/setWebViewReady", true);
  });

  // 브라우저 백 버튼 처리 (모바일/PC 브라우저)
  // Flutter InAppWebView 환경에서는 popstate 이벤트가 발생하지 않을 수 있으므로 별도 처리 불필요
  window.addEventListener('popstate', handlePopState);

  // Flutter 백키 전달 (nativeBack): 웹 모달/다이얼로그가 있으면 닫고 backKeyHandled(true), 없으면 backKeyHandled(false)
  window.addEventListener('nativeBack', handleNativeBack);
});
onBeforeUnmount(() => {
  if (store.getters["isLoading"]) {
    return false;
  }
  console.log('App.onBeforeUnmount');

  // 컴포넌트 언마운트 시 body 스크롤 복원
  document.body.style.overflow = '';

  if (window.addEventListener) {
    window.removeEventListener('message', onReceiveMessage);
    window.removeEventListener('popstate', handlePopState);
    window.removeEventListener('nativeBack', handleNativeBack);
  } else if (window.attachEvent) {
    window.detachEvent('message', onReceiveMessage);
    window.detachEvent('popstate', handlePopState);
    window.detachEvent('nativeBack', handleNativeBack);
  }
});

function handleNativeBack() {
  if (store.getters['visible']) {
    store.dispatch('hide');
    if (window.flutter_inappwebview) {
      window.flutter_inappwebview.callHandler('backKeyHandled', true);
    }
    return;
  }
  window.__nativeBackHandled = false;
  window.dispatchEvent(new Event('nativeBackRequest'));
  setTimeout(() => {
    if (window.__nativeBackHandled !== true && window.flutter_inappwebview) {
      window.flutter_inappwebview.callHandler('backKeyHandled', false);
    }
  }, 100);
}
const onReceiveMessage = (payload) => {
  try {
    if (payload?.data?.source === "react-devtools-content-script") {
      return;
    }

    const json = JSON.parse(decodeURIComponent(window.atob(payload.data)));
    logger.log(`${json}`);
    if (json) {
      const { result, callback, data, resultCode, msg } = json;
      if (callback) {

        let decodedMsg = msg;
        if (msg) {
          try {
            // msg가 깨진 문자열인 경우, 바이트 배열로 변환 후 UTF-8 디코딩
            const binaryString = msg;
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            decodedMsg = new TextDecoder('utf-8').decode(bytes);
          } catch (e) {
            // 디코딩 실패 시 원본 사용
            decodedMsg = msg;
          }
        }

        logger.log(`App.onReceiveMessage = > callback : ${callback}(${JSON.stringify({ result, data, resultCode, msg: decodedMsg })})`);
        logger.log(`${callback}(${decodedMsg}))`);
        emitter.emit(callback, { result, data, resultCode, msg: decodedMsg });

        if (callback == "onAppRestarted" && isLogin.value) {
          store.dispatch('home/fetchAllZones');
        }
      }
    }
  } catch (e) {
    console.error(e)
  }
};
router.beforeEach(async (to, from, next) => {
  console.log(`App.router.beforeEach to:${to.path} from:${from.path}`);
  console.log(isCertified.value, isLogin.value);

  if (isLogin.value) {
    if (isCertified.value != undefined && isCertified.value == false && (
      to.path !== "/login" &&
      to.path !== "/join" &&
      to.path !== "/find/password" &&
      to.path !== "/change/password" &&
      to.path !== "/change/password/complete" &&
      to.path !== "/auth/cert" &&
      to.path !== "/auth/cert/complete")) {
      router.push('/auth/cert');
      return;
    }
  }

  if (store.getters["isLoading"]) {
    next(false);
    return;
  }

  progress.value = useProgress().start();
  store.state.options.appSidebarMobileToggled = false;
  document.body.scrollTop = 0;
  document.documentElement.scrollTop = 0;

  var targetElm = [].slice.call(
    document.querySelectorAll('.app-sidebar .menu-submenu'),
  );
  targetElm.map(function (elm) {
    elm.style.display = '';
  });

  emitter.emit('hidePopAlert');
  next();
});

router.afterEach(async (to, from) => {
  console.log(`App.router.afterEach to:${to.path} from:${from.path}`);
  progress.value.finish();
});

emitter.on('showPopAlert', (params) => {
  // 기본은 확인만. 호출부에서 showCancel/showOk 로 오버라이드 가능
  store.dispatch('show', { showCancel: false, showOk: true, ...params });
});
emitter.on('hidePopAlert', () => {
  store.dispatch('hide');
});

emitter.on('showPopConfirm', (params) => {
  store.dispatch('show', params);
});
emitter.on('hidePopConfirm', () => {
  store.dispatch('hide');
});

emitter.on('showPopToast', (params) => {
  store.dispatch('toast/show', params);
});

emitter.on('showLoading', () => {
  spinnerVisible.value = true;
  loadingCount.value++;
  console.log('loadingCount: ', loadingCount.value);
  // logger.log('loadingCount: ' + loadingCount.value);
  store.commit('setIsLoading', spinnerVisible.value && loadingCount.value > 0);
});
emitter.on('hideLoading', (force = false) => {
  loadingCount.value--;
  if (force) {
    loadingCount.value = 0;
  }
  console.log('loadingCount: ', loadingCount.value);
  // logger.log('loadingCount: ' + loadingCount.value);
  if (loadingCount.value <= 0) {
    spinnerVisible.value = false;
    loadingCount.value = 0;
    loadingText.value = '';
  }
  store.commit('setIsLoading', spinnerVisible.value && loadingCount.value > 0);
});
emitter.on('setLoadingText', (text) => {
  // if (loadingCount.value <= 0) {
  //   return;
  // }
  loadingText.value = text;
});
emitter.on('appendLoadingText', (text) => {
  loadingText.value += '\n' + text;
});

watch(controllers, () => {
  if (
    controllers.value.length > 0 &&
    route.path !== '/update' &&
    !isShowedFirmwarePopup
  ) {
    isShowedFirmwarePopup = true;

    setTimeout(() => {
      emitter.emit('showPopConfirm', {
        title: '펌웨어 업데이트',
        description: `현재 펌웨어 버전이 최신 버전보다 낮습니다.\n펌웨어 업데이트를 하시겠습니까?`,
        okCallback: () => router.push('/update'),
      });
    }, 3000);
  }
});

// 사이드바 열림 상태를 watch하여 body 스크롤 제어
watch(() => getters.value.appSidebarMobileToggled, (isOpen) => {
  const appContent = document.querySelector('.app-content');
  
  if (isOpen) {
    // 현재 스크롤 위치 저장
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.body.setAttribute('data-scroll-y', scrollY.toString());
    
    // app-content 영역 스크롤 차단
    if (appContent) {
      appContent.style.overflow = 'hidden';
      appContent.style.pointerEvents = 'none';
    }
    
    // 터치 이벤트로 인한 스크롤 차단
    touchMoveHandler = (e) => {
      // 사이드바 내부가 아닌 경우에만 차단
      if (!e.target.closest('.app-sidebar')) {
        e.preventDefault();
      }
    };
    document.addEventListener('touchmove', touchMoveHandler, { passive: false });
    document.addEventListener('wheel', touchMoveHandler, { passive: false });
  } else {
    // 스크롤 복원
    const scrollY = document.body.getAttribute('data-scroll-y') || '0';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    document.body.removeAttribute('data-scroll-y');
    
    // app-content 영역 복원
    if (appContent) {
      appContent.style.overflow = '';
      appContent.style.pointerEvents = '';
    }
    
    // 터치 이벤트 리스너 제거
    if (touchMoveHandler) {
      document.removeEventListener('touchmove', touchMoveHandler);
      document.removeEventListener('wheel', touchMoveHandler);
      touchMoveHandler = null;
    }
    
    if (scrollY) {
      window.scrollTo(0, parseInt(scrollY));
    }
  }
});
</script>

<style scoped>
* {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
</style>